import { useState, useEffect, useId, useCallback } from "react";
import {
	Label,
	Textarea,
	Checkbox,
	useThemeMode,
	Spinner,
	Tooltip
	// Select
} from "flowbite-react";
import toast from 'react-hot-toast';
import { useRoadmapService, useOrganizationService } from "../../services";
import { useJwt } from "../../helpers/common";
import { Drawer } from "../../components/Modals";
import { useSelector, useDispatch } from "react-redux";
import { getTimeZoneDate, formatDateBy } from "../../helpers/data-format";
import DatePicker from "react-datepicker";
import CustomButton from '../../components/common/CustomButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Select, { MultiValue, SingleValue, components } from 'react-select';
import RichTextEditor from '../../components/RichTextEditor';

import {
	setSingleRoadmap
} from '../../redux/slices/roadmap';
import { setSearchTerm } from '../../redux/slices/organization';

// Enhanced Project Skeleton Component that exactly matches the actual project items
const EnhancedProjectSkeleton = () => {
	return (
		<div className="flex">
			{/* Checkbox skeleton */}
			<div className="w-4 h-4 mr-4 bg-gray-300 dark:bg-gray-600 border border-gray-300 dark:border-gray-700 rounded animate-pulse"></div>
			{/* Project item skeleton */}
			<div className="grid grid-cols-5 p-2 items-center relative group rounded-md border border-gray-700 dark:bg-slate-800 bg-gray-50 w-full">
				<div className="flex col-span-2">
					<div className="ml-10">
						<div className="h-2 w-14 bg-gray-300 dark:bg-gray-600 rounded mb-2 opacity-40 animate-pulse"></div>
						<div className="h-4 w-36 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
					</div>
				</div>
				<div>
					<div className="h-2 w-16 bg-gray-300 dark:bg-gray-600 rounded mb-2 opacity-40 animate-pulse"></div>
					<div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
				</div>
				<div>
					<div className="h-2 w-16 bg-gray-300 dark:bg-gray-600 rounded mb-2 opacity-40 animate-pulse"></div>
					<div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
				</div>
			</div>
		</div>
	);
};

// Interface for TabPanel
interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

const tabmapping = [
	{
		label: 'Gantt View',
		value: "gantt"
	},
	{
		label: 'Task View',
		value: "tasklist"
	},

]

interface CreateRoadMapsModalProps {
	setShowModal?: any,
	setSelectedRoadmap?: any,
	isVisible?: any,
	roadmap?: any
	org_id?: any
}

interface Milestone {
	_uid: string;
	name: string;
	color: string;
	start_date: Date;
	id: number;
}

interface Dependency {
	_uid: string;
	project_to: {
		value: string;
		label: string;
		start_date: any;
		project_end_date: any;
	};
	project_on: any[];
}

interface ProjectData {
	id?: string;
	name?: string;
	description?: string;
	start_date?: string;
	project_end_date?: string;
	project_ids?: string[];
	[key: string]: any; // Allow for additional properties
}

const CustomTabPanel = (props: TabPanelProps) => {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

const CreateRoadMapsModal = ({ setShowModal, setSelectedRoadmap, isVisible = false, roadmap = {} }: CreateRoadMapsModalProps) => {
	const { id: roadmap_id = '' } = roadmap;
	const JWTdata: any = useJwt()
	const org_id = roadmap.org_id || JWTdata.org_id;
	const [milestones, setMilestones] = useState<Milestone[]>([])
	const [dependencies, setDependencies] = useState<Dependency[]>([])
	const [selectedProjects, setSelectedProjects] = useState<ProjectData[]>([])
	const [payloadData, setPayloadData] = useState<ProjectData>({
		name: '',
		description: '',
		project_ids: []
	})
	const [searchQuery, setSearchQuery] = useState("");
	const [activeTabs, setActiveTabs] = useState(0);
	const [mode] = useThemeMode();
	const dispatch = useDispatch();

	const { saveRoadmap, updateRoadmap, fetchSingleRoadmap } = useRoadmapService();
	const { fetchAllProjects } = useOrganizationService();

	const {
		saveRoadmapLoading,
		singleRoadmapLoading,
		singleRoadmapRes = {},
		sim_flag
	} = useSelector((state: any) => state.roadmap);

	const {
		data: projects,
		loading: orgLoading,
		loadingMore = false,
		hasMore = false,
		page = 1,
		totalPages = 1,
		searchTerm = ''
	} = useSelector((state: any) => state.organization);

	useEffect(() => {
		// Keep local search query and Redux searchTerm in sync
		setSearchQuery(searchTerm);
	}, [searchTerm]);

	useEffect(() => {
		if (roadmap_id) {
			setPayloadData({ ...singleRoadmapRes })
			if (singleRoadmapRes.project_ids) {
				setSelectedProjects(singleRoadmapRes.projects)
				// setSelectedProjects(singleRoadmapRes.project_ids)
			}
			if (singleRoadmapRes?.milestones) {
				setMilestones(singleRoadmapRes.milestones)
			}
			if (singleRoadmapRes?.dependencies) {
				const d = transformProjectDependencyData(singleRoadmapRes?.dependencies)
				setDependencies(d)
			}
		}

	}, [singleRoadmapRes, orgLoading])

	useEffect(() => {
		if (org_id && !orgLoading) {
			fetchAllProjects(org_id, true, 1, searchTerm);
		}
	}, [org_id, searchTerm]);

	useEffect(() => {
		if (roadmap_id) {
			fetchSingleRoadmap(roadmap_id, sim_flag, false)
		}
	}, [roadmap_id]);

	const transformProjectDependencyData = (data: any[]) => {
		return data.map(project => {
			const dependencies = project.dependency || [];
			const projectInfo = selectedProjects.find(p => p.project_id === project.project_id) || {};

			// project_to is project and project_on is its dependency
			return {
				_uid: `${new Date().getTime().toString()}_${Math.floor(100000 + Math.random() * 900000)}`,
				project_to: {
					value: project.project_id,
					label: projectInfo?.project_name || "",
					start_date: projectInfo?.start_date,
					project_end_date: projectInfo?.end_date
				},
				project_on: dependencies.map((dep: { project_id: any; }) => {
					const projectInfo = selectedProjects.find(p => p.project_id === dep.project_id) || {};
					return {
						value: dep.project_id,
						label: projectInfo.project_name || "",
						project_id: dep.project_id,
						project_end_date: projectInfo?.start_date,
						project_start_date: projectInfo?.end_date
					};
				})
			};
		});
	}


	const handleOnChange = (e: any) => {
		const { name, value } = e.target;
		const data = { ...payloadData, [name]: value }
		setPayloadData(data)
	}

	const handleDescriptionChange = (value: string) => {
		const data = { ...payloadData, description: value };
		setPayloadData(data);
	}

	const handleProjectCheckbox = (e: any, project?: any) => {
		setSelectedProjects(prev => {
			if(e.target.checked) {
				return [...prev, {
					project_id: project.project_id,
					project_name: project.project_id,
					start_date: project.start_date,
					end_date: project.end_date,
				}]
			} else {
				return prev.filter(proj => proj.project_id !== project.project_id)
			}
		})
	}

	const onSubmit = async () => {
		const isMilestonesFlag = checkForEmptyKeys(milestones)
		const dependenciesData = createDependenciesPayload()
		let d: any = {
			"name": payloadData?.name || '',
			"projects": selectedProjects.map(proj => proj.project_id),
			"description": payloadData?.description || '',
			dependencies: dependenciesData,
			milestones,
		}
		if (saveRoadmapLoading) {
			toast.error("Backend is processing something.");
			return
		}

		if (!d.name) {
			toast.error("Please fill Strategic Map name.");
			return
		}

		// Improved validation for rich text editor content
		const strippedDescription = d.description?.replace(/<[^>]*>/g, '').trim();
		if (!d.description || !strippedDescription || d.description === '<p></p>' || d.description === '<p><br></p>' || d.description === '<p><br/></p>') {
			toast.error("Please fill Strategic Map description.");
			return
		}
		if (!selectedProjects.length) {
			toast.error("Please select atleast one project.");
			return
		}
		if (!selectedProjects.length) {
			toast.error("Please select atleast one project.");
			return
		}
		if (!isMilestonesFlag) {
			toast.error("Please fill mandatory field in milestone.");
			return
		}
		if (payloadData.id) {
			await updateRoadmap(payloadData.id, { ...d });
		} else {
			await saveRoadmap({ ...d, ...payloadData });
		}
		handleCancel()
	}

	const handleCancel = () => {
		setSelectedProjects([])
		setDependencies([])
		setMilestones([])
		setPayloadData({})
		setSelectedRoadmap({})
		dispatch(setSingleRoadmap({}))
		setShowModal(false)
	}

	const handleAddMilestoneClick = () => {
		const _uid = new Date().getTime().toString();
		const data: any = [...milestones];
		data.push({
			_uid,
			name: "",
			color: "#000000",
			start_date: null,
			id: 0
		})
		setMilestones(data)
	}
	const handleAddDependenciesClick = () => {
		const _uid = new Date().getTime().toString();
		const data: any = [...dependencies];
		data.push({
			_uid,
			project_to: '',
			project_on: [],
		})
		setDependencies(data)
	}

	const handleOnDateChange = (date: any, index: number) => {
		try {
			const data: any = [...milestones];
			const _cur = { ...data[index] } || {};
			_cur["start_date"] = date;
			data[index] = _cur;
			setMilestones(data);
		} catch (error) {
			console.log("error   ", error);
		}

	}
	const handleMilestoneChange = (e: any, index: number) => {
		const data: any = [...milestones];
		const val = e.target.value
		const name = e.target.name
		const _cur = { ...data[index] } || {};
		_cur[name] = val;
		data[index] = _cur;
		setMilestones(data);
	}
	const handleMilestoneClick = (i: number) => {
		const data: any = [...milestones];
		const payload = data.filter((d, index: number) => index !== i)
		setMilestones(payload);
	}

	const handleDependenciesClick = (i: number) => {
		const data: any = [...dependencies];
		const payload = data.filter((d, index: number) => index !== i)
		setDependencies(payload);
	}

	const checkForEmptyKeys = (_d) => {
		let flag = true;
		_d.forEach(element => {
			if (!element.name) {
				flag = false
			}
			if (!element.start_date) {
				flag = false
			}
		});
		return flag;
	}

	const createDependenciesPayload = () => {
		let data: any = []
		dependencies.forEach((depend: any) => {
			if (depend?.project_to && depend.project_on.length) {
				const d = {
					"dependency": [...depend.project_on],
					"project_id": depend?.project_to?.value,
					"project_start_date": depend?.project_to?.start_date,
					"project_end_date": depend?.project_to?.start_date
				}
				data.push(d)
			}

		})
		return data
	}

	const handleTabsToggle = (e, tab) => {
		setActiveTabs(tab)
	};

	const handleOnSelectChange = (value: SingleValue<string>, uid: any) => {
		const data: any = [...dependencies];
		const referenceProject = data.find(project => project.project_to.value === value.value);
		if (!referenceProject) {
			const updatedProjects = data.map((project: { _uid: any; }) =>
				project._uid === uid ? { ...project, project_to: value, project_on: [] } : project
			);
			setDependencies(updatedProjects)
		} else {
			toast.error("The project has already been selected.");
		}


	}
	const handleOnMultiSelectChange = (value: MultiValue<string>, uid: any) => {
		const data: any = [...dependencies];
		const updatedProjects = data.map((project: { _uid: any; }) =>
			project._uid === uid ? { ...project, project_on: value } : project
		);
		setDependencies(updatedProjects)
	}

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		// Update local state immediately
		setSearchQuery(value);

		// Update Redux store's searchTerm
		dispatch(setSearchTerm(value));

		// Call API directly for immediate response with current input value
		// This ensures even fast typing sends every character to the backend
		if (org_id && !orgLoading) {
			fetchAllProjects(org_id, true, 1, value);
		}
	};

	const handleClearSearch = () => {
		setSearchQuery('');
		dispatch(setSearchTerm(''));

		// Call API directly when clearing search
		if (org_id && !orgLoading) {
			fetchAllProjects(org_id, true, 1, '');
		}
	};

	const getProjectList = () => {
		const optionsArr: any = []
		selectedProjects.forEach((element: { project_id: any; project_name: any; end_date: any, start_date: any }) => {
			if(!dependencies.map(dependency => dependency.project_to.value).includes(element.project_id)) {
				optionsArr.push({
					value: element.project_id,
					project_id: element.project_id,
					label: element.project_name,
					project_end_date: element.end_date,
					start_date: element.start_date
				})
			}
		})
		return optionsArr
	}

	const getFilterProjectList = (project_id: any) => {
		const optionsArr: any = [];

		// Find the reference project details
		const referenceProject = selectedProjects.find(project => project.project_id === project_id);

		if (!referenceProject) return optionsArr; // Return empty if project_id not found

		const { start_date: refStartDate, end_date: refEndDate } = referenceProject;

		if (selectedProjects.length) {
			selectedProjects.forEach((element: { project_id: any; project_name: any; start_date: any; end_date: any }) => {
                const hasElementStartedBefore = new Date(element.start_date) <= new Date(refStartDate)
                const hasElementEndedBefore = new Date(element.end_date) <= new Date(refEndDate)
				const isEnabled = element.project_id !== project_id && hasElementStartedBefore && hasElementEndedBefore

                let errorText = ""

                if(!isEnabled) {
					if(hasElementStartedBefore) {
						if(hasElementEndedBefore) {
							errorText = "Cannot select projects which start before and end before this project"
						} else {
							errorText = "Cannot select projects which start before and end after this project"
						}
					} else {
						if(hasElementEndedBefore) {
							errorText = "Cannot select projects which start after and end before this project"
						} else {
							errorText = "Cannot select projects which start after and end after this project"
						}
					}
                }

				optionsArr.push({
					value: element.project_id,
					project_id: element.project_id,
					label: element.project_name,
					project_start_date: element.start_date,
					project_end_date: element.end_date,
					isDisabled: !isEnabled,
					error: errorText
				});
			});
		}
		return optionsArr.sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled));;
	}


	const simHeaderBg = `bg-white dark:bg-[#111928]`;

	const getSortedProjects = () => {
		// Removing local filtering and using backend filtered results directly
		return [...projects].sort((a, b) => {
			// First sort by start_date
			const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
			const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;

			if (dateA !== dateB) {
				return dateA - dateB; // Sort by date first
			}

			// If dates are equal, sort by project name
			return a.project_name.toLowerCase().localeCompare(b.project_name.toLowerCase());
		});
	};

	return (
		<>
			<Drawer
				isOpen={isVisible}
				onClose={() => setShowModal(false)}
				position="right"
			>
				<div className="relative h-[100vh] flex flex-col">

					<div className={`flex flex-row border-b border-gray-200 !p-6 dark:border-gray-700 ${simHeaderBg} sticky top-0 z-40`}>
						<div className="flex w-full h-12 items-center  ">

							<div className="dark:text-white text-gray-700 text-lg font-bold">{`${roadmap_id ? "Update " : "New "}`}strategic map</div>
							<button type="button" className="ml-auto absolute right-[10px] top-0 dark:text-white text-gray-700 w-6 h-6 rounded-full bg-black text-sm" onClick={handleCancel}>
								X
							</button>
						</div>
					</div>
					{singleRoadmapLoading ? (
						<div className="text-center w-full h-full bottom-0 p-5">
							<Spinner aria-label="Center-aligned spinner" />
						</div>
					) :
						<div className="px-6 py-2 min-h-[80vh] max-h-[90vh] overflow-y-auto">
							<div className="mb-5">
								<Label htmlFor="roadmapname">Strategic map name*</Label>
								<div className="mt-1">
									<div className="mb-2">
										<input type="text" id="roadmapname"
											className="block w-full p-2 disabled:pointer-events-none text-gray-900 border border-gray-300  bg-gray-50 sm:text-md focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:border-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 rounded-md"
											onChange={handleOnChange}
											name="name"
											placeholder="Strategic map name"
											defaultValue={payloadData.name}
											disabled={saveRoadmapLoading}
										/>
									</div>
								</div>
							</div>
							<div className="mb-5">
								<Label htmlFor="projectname">Strategic map description*</Label>
								<div className="mt-1">
									<div className="mb-2">
										<RichTextEditor
											content={payloadData.description || ''}
											onChange={handleDescriptionChange}
											disabled={saveRoadmapLoading}
											placeholder="Strategic map description"
										/>
									</div>
								</div>
							</div>
							<Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '10px' }} className="text-gray-800 dark:text-white">
								<Tabs value={activeTabs} onChange={handleTabsToggle} aria-label="">
									<Tab
										label="Project"
										{...a11yProps(0)}
										sx={{ color: mode === "dark" ? "white" : "gray" }}
									/>
									<Tab
										label="MileStone"
										{...a11yProps(1)}
										sx={{ color: mode === "dark" ? "white" : "gray" }}
									/>
									{selectedProjects.length ? <Tab
										label="Dependencies"
										{...a11yProps(2)}
										sx={{ color: mode === "dark" ? "white" : "gray" }}
									/> : ""}
								</Tabs>
							</Box>

							<CustomTabPanel value={activeTabs} index={0}>
								<div className="flex flex-col gap-5">
									<div className="mb-2">
										<Label htmlFor="project-search">Search Projects</Label>
										<div className="relative">
											<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
												<svg aria-hidden="true" className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
												</svg>
											</div>
											<input
												type="text"
												id="project-search"
												className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:border-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
												placeholder="Search projects..."
												value={searchQuery}
												onChange={handleSearchChange}

											/>
											{searchQuery && (
												<button
													className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
													onClick={handleClearSearch}
													title="Clear search"
												>
													<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<line x1="18" y1="6" x2="6" y2="18"></line>
														<line x1="6" y1="6" x2="18" y2="18"></line>
													</svg>
												</button>
											)}
										</div>
									</div>

									{/* Main Content Area */}
									<div>
										{/* Initial loading state for projects list */}
										{orgLoading && projects.length === 0 ? (
											<div className="flex flex-col gap-4">
												{[...Array(10)].map((_, index) => (
													<EnhancedProjectSkeleton key={`initial-skeleton-${index}`} />
												))}
											</div>
										) : projects.length === 0 ? (
											<div className="text-center p-4 dark:text-white text-gray-700">
												{searchQuery ? "No projects found matching your search" : "No projects found"}
											</div>
										) : (
											// Display the filtered and sorted projects
											<div className="flex flex-col gap-4">
												{getSortedProjects().map((project: any) => (
													<div className="flex" key={project.project_id}>
														<Checkbox
															id={project.project_id}
															defaultChecked={selectedProjects && selectedProjects.map(el => el.project_id).includes(project?.project_id)}
															checked={selectedProjects && selectedProjects.map(el => el.project_id).includes(project?.project_id)}
															className="mr-4 cursor-pointer"
															onChange={(e) => handleProjectCheckbox(e, project)}
														/>
														<div className="grid grid-cols-5 p-2 items-center relative group rounded-md border border-gray-700 dark:bg-slate-800 bg-gray-50 w-full">
															<div className="flex col-span-2">

																<div className="ml-10 dark:text-white text-gray-700">
																	<div className="opacity-40 text-xs font-normal font-['Inter'] uppercase mb-1.5">Project</div>
																	<Tooltip trigger={'hover'} content={
																		<div className=" text-sm font-normal font-['Inter'] capitalize" title={project.project_name}>{project.project_name}</div>
																	}>
																		<div className=" text-sm font-normal font-['Inter'] capitalize truncate w-36" title={project.project_name}>{project.project_name}</div>
																	</Tooltip>
																</div>
															</div>
															<div className="dark:text-white text-gray-700">
																<div className="opacity-40  text-xs font-normal font-['Inter'] uppercase mb-1.5">Start Date</div>
																<div className=" text-sm font-normal font-['Inter'] capitalize truncate">{project?.start_date ? formatDateBy(project?.start_date) : '...'}</div>
															</div>
															{/* <div>
                                            <div className="opacity-40 text-white text-xs font-normal font-['Inter'] uppercase mb-1.5">End Date</div>
                                            <div className="text-white text-sm font-normal font-['Inter'] capitalize truncate">{project.start_date}</div>
                                        </div> */}
															<div>
																<div className="opacity-40 text-white text-xs font-normal font-['Inter'] uppercase mb-1.5">Project Stage</div>
																<div className="text-white text-sm font-normal font-['Inter'] capitalize truncate">{project.stage}</div>
															</div>
														</div>
													</div>
												))}
											</div>
										)}

										{/* Show loading skeletons when loading more */}
										{loadingMore && (
											<div className="flex flex-col gap-4 mt-4">
												{[...Array(3)].map((_, index) => (
													<EnhancedProjectSkeleton key={`loading-more-${index}`} />
												))}
											</div>
										)}

										{/* Load More button */}
										{projects.length > 0 && hasMore && !loadingMore && (
											<div className="flex justify-center mt-6 mb-2">
												<button
													className={`flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${loadingMore
														? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
														: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
														}`}
													onClick={() => {
														if (!loadingMore && hasMore) {
															// Use the current search term when loading more
															fetchAllProjects(org_id, false, page + 1, searchTerm);
														}
													}}
													disabled={loadingMore}
												>
													{loadingMore ? (
														<>
															<svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
																<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
																<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
															</svg>
															Loading...
														</>
													) : (
														<>
															Load More Projects
															<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
																<path d="M7 13l5 5 5-5"></path>
																<path d="M7 6l5 5 5-5"></path>
															</svg>
														</>
													)}
												</button>
											</div>
										)}

										{/* Show loading indicator when loading more */}
										{loadingMore && (
											<div className="text-center py-2 text-sm text-blue-600 dark:text-blue-400">
												Loading more projects...
											</div>
										)}
									</div>
								</div>
							</CustomTabPanel>
							<CustomTabPanel value={activeTabs} index={1}>
								<div className="flex flex-col gap-5 ">
									<div className="dark:text-white text-gray-700 text-lg font-bold flex font-['Inter'] border-b dark:border-gray-600 border-gray-300 pb-5">
										<div className="">Milestone</div>
										<div className="ml-auto cursor-pointer  hover:text-blue-700 text-sm" onClick={handleAddMilestoneClick}> + Add </div>
									</div>
									<div className="flex flex-col">
										{milestones.map((milestone: any, index) => {
											return (<div className="flex gap-10 justify-start items-center relative" key={milestone._uid}>
												<div className="mb-5">
													<Label htmlFor="milestone_name">Name *</Label>
													<div className="mt-1">
														<div className="mb-2">
															<input type="text" id="milestone_name"
																className="block w-full p-2 disabled:pointer-events-none text-gray-900 border border-gray-300  bg-gray-50 sm:text-md focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:border-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
																onChange={e => handleMilestoneChange(e, index)}
																name="name"
																placeholder="Name"
																defaultValue={milestone.name}
															/>
														</div>
													</div>
												</div>
												<div className="mb-5">
													<Label htmlFor="milestone_date">Date *</Label>
													<div className="mt-1">
														<div className="mb-2">
															<DatePicker
																id="milestone_date"
																className="bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 block w-full p-2 dark:bg-slate-800 dark:border-gray-700 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
																selected={(milestone?.start_date !== null && milestone?.start_date !== undefined && milestone?.start_date !== "None") ? new Date(milestone.start_date) : null}
																onChange={(date: any) => handleOnDateChange(date, index)}
																autoComplete='off'
															/>
														</div>
													</div>
												</div>
												<div className="mb-5">
													<Label htmlFor="milestone_color">Color</Label>
													<div className="mt-1">
														<div className="mb-2">
															<input
																type="color"
																id="milestone_color"
																onChange={e => handleMilestoneChange(e, index)}
																name="color"
																placeholder="Color"
																value={milestone.color}
															/>
														</div>
													</div>
												</div>
												<div
													className=" right-0 top-8 bg-red-600 text-white rounded-full w-4 h-4 text-xs px-1 cursor-pointer"
													onClick={() => handleMilestoneClick(index)}
												>X
												</div>

											</div>
											)
										})}

									</div>
								</div>
							</CustomTabPanel>
							{activeTabs === 2 && <CustomTabPanel value={activeTabs} index={2}>
								<div className="flex flex-col gap-5 ">
									<div className="dark:text-white text-gray-700 text-lg font-bold flex font-['Inter'] border-b dark:border-gray-600 border-gray-300 pb-5">
										<div className="">Dependencies</div>
										<div className="ml-auto cursor-pointer  hover:text-blue-700 text-sm" onClick={handleAddDependenciesClick}> + Add </div>
									</div>
									<div className="flex flex-col">
										{dependencies.map((dependencie: any, index) => {
											return (
												<div className="grid grid-cols-2 gap-10 justify-start items-start relative" key={`${dependencie._uid}_${index}`}>
													<div className="flex flex-col mb-4   w-full">
														<Label htmlFor="team_size" className="block text-sm font-medium ">Project</Label>
														<Select
															className="rounded-md w-full"
															isMulti={false}
															classNamePrefix="resourcesContainer"
															defaultValue={dependencie.project_to}
															value={dependencie.project_to}
															onChange={(value) => handleOnSelectChange(value, dependencie._uid)}
															options={getProjectList()}
															isClearable={false}
														/>
													</div>
													<div className="flex flex-col mb-4   w-full">
														<Label htmlFor="team_size" className="block text-sm font-medium ">Depends On</Label>
														<Select
															className="rounded-md"
															isMulti
															classNamePrefix="resourcesContainer"
															defaultValue={dependencie.project_on}
															value={dependencie.project_on}
															onChange={(value) => handleOnMultiSelectChange(value, dependencie._uid)}
															options={getFilterProjectList(dependencie?.project_to?.value)}
															isClearable={false}
															components={{
																Option: (props) => {
																	const { data, innerRef, innerProps } = props;

																	return data.isDisabled ? (
																		<div
																			ref={innerRef}
																			{...innerProps}
																			className="cursor-not-allowed px-2 py-1"
																		>
																			<Tooltip content={data.error}>
																				<span className="opacity-60 ">{data.label}</span>
																			</Tooltip>
																		</div>
																	) : (
																		<components.Option {...props} />
																	);
																}
															}}
														/>
														<Tooltip placement="bottom" className="" content={`Try this prompt`}/>
													</div>
													<div
														className="absolute right-[-10px] top-0 bg-red-600 text-white rounded-full w-4 h-4 text-xs px-1 cursor-pointer"
														onClick={() => handleDependenciesClick(index, dependencie._uid)}
													>X
													</div>

												</div>
											)
										})}

									</div>
								</div>
							</CustomTabPanel>
							}
						</div>
					}
					<div className="px-6 py-2 items-center flex gap-5 mt-auto border-t border-gray-200 dark:border-gray-700">
						<CustomButton
							variant="cancel"
							onClick={handleCancel}
						>
							Cancel
						</CustomButton>
						<CustomButton
							onClick={onSubmit}
							disabled={saveRoadmapLoading}
							isLoading={saveRoadmapLoading}
							className="ml-auto"
						>
							{roadmap_id ? "Update" : "Add"}
						</CustomButton>
					</div>
				</div>
			</Drawer>
		</>
	)

}

export default CreateRoadMapsModal;

